'use client';

// ShareEntityModal — opens the post composer with a target entity pre-attached
// and a Copy-link affordance in the toolbar. Replaces the legacy
// per-entity-type share dialogs (resource → ShareToCircleModal, event →
// ShareMenu, post → ShareMenu) with a single shape.
//
// Resolution happens lazily on open via the same endpoint the composer's
// paste-URL flow uses (`/api/v1/posts/attachments/resolve`); cached by React
// Query so re-opens are instant.

import { useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { useQuery } from '@tanstack/react-query';
import { Body, Box, Button, I, Row, Spinner, Stack } from '@leanin/ui';
import { Dialog, DialogSection } from '@leanin/ui/client';
import type {
  AttachableInputKind,
  AttachedEntityView,
} from '@leanin/shared';
import { useOptionalViewer, type ViewerIdentity } from '@/lib/viewer-context';
import { useComposerGroups } from '@/lib/queries/composer';
import { miscKeys } from '@/lib/queries/misc';
import { resolveShareUrl } from '@/lib/share-url';
import {
  trackContentSharedToGroup,
  trackShareLinkCopied,
} from '@/lib/analytics/events';
import { PostComposerSheet } from './PostComposer';

// Share-as-post is offered only for entity kinds the composer can attach
// (`AttachableInputKind`). Circles and Networks were removed from the share
// surface — see `ATTACHED_ENTITY_INPUT_KINDS` on the server.
export type ShareEntityRef = {
  readonly kind: AttachableInputKind;
  readonly id: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: ShareEntityRef;
};

// Client-side canonical path per attachable kind — mirrors the hrefs the
// server-side resolvers emit (`resources`/`identity`/`groups` resolve-cards).
// Used only for the anonymous link-only dialog, where the authed resolve
// endpoint isn't available.
const ENTITY_PATH: Record<AttachableInputKind, string> = {
  resource: '/resources',
  event: '/events',
  profile: '/profile',
  post: '/posts',
};

export function ShareEntityModal({ open, onOpenChange, entity }: Props) {
  const viewer = useOptionalViewer();
  // Anonymous visitors can't share-as-post (the composer and the attachment
  // resolver are member surfaces) — they get the copy-link dialog instead.
  if (viewer === null) {
    return (
      <AnonShareDialog open={open} onOpenChange={onOpenChange} entity={entity} />
    );
  }
  return (
    <AuthedShareModal
      open={open}
      onOpenChange={onOpenChange}
      entity={entity}
      viewer={viewer}
    />
  );
}

function AnonShareDialog({ open, onOpenChange, entity }: Props) {
  const t = useTranslations('Feed');
  const href = `${ENTITY_PATH[entity.kind]}/${entity.id}`;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('share.modalTitle')}
      description={t('share.linkOnlyDescription')}
      size="sm"
    >
      <DialogSection>
        <Row gap="sm" align="center" justify="space-between">
          <Body size="sm" color="mute" style={{ overflowWrap: 'anywhere' }}>
            {resolveShareUrl(href)}
          </Body>
          <CopyLinkButton href={href} kind={entity.kind} />
        </Row>
      </DialogSection>
    </Dialog>
  );
}

function AuthedShareModal({
  open,
  onOpenChange,
  entity,
  viewer,
}: Props & { viewer: ViewerIdentity }) {
  const t = useTranslations('Feed');
  const groups = useComposerGroups({ enabled: open });
  const resolve = useResolveAttachment(entity, open);

  const composerViewer = {
    id: viewer.id,
    name: viewer.displayName,
    ...(viewer.photoUrl !== null ? { photoUrl: viewer.photoUrl } : {}),
  };

  // Per-entity draft scope — body + media drafts persist across opens for the
  // same shared entity but never collide with the inline composer's drafts.
  const draftKey = `composer-share-v1-${viewer.id}-${entity.kind}-${entity.id}`;

  const close = () => onOpenChange(false);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('share.modalTitle')}
      description={t('share.modalDescription')}
      size="md"
    >
      <DialogSection bleed scrollable>
        {resolve.isLoading ? (
          <ResolveLoading />
        ) : resolve.attachment === null ? (
          <ResolveError onClose={close} />
        ) : (
          <PostComposerSheet
            viewer={composerViewer}
            myGroups={groups.data ?? []}
            initialAttached={[resolve.attachment]}
            draftKeyOverride={draftKey}
            chrome="flush"
            onClose={close}
            onPosted={({ audience }) =>
              trackContentSharedToGroup({
                entity_type: entity.kind,
                share_destination_type: audience,
              })
            }
            toolbarExtras={
              <CopyLinkButton
                href={resolve.attachment.href}
                kind={entity.kind}
              />
            }
          />
        )}
      </DialogSection>
    </Dialog>
  );
}

// ── Resolve helpers ──────────────────────────────────────────────────────

type ResolveResult =
  | { isLoading: true; attachment: null }
  | { isLoading: false; attachment: AttachedEntityView | null };

function useResolveAttachment(
  entity: ShareEntityRef,
  enabled: boolean,
): ResolveResult {
  const query = useQuery({
    ...miscKeys.shareResolveAttachment(entity.kind, entity.id),
    enabled,
    staleTime: 60_000,
  });
  if (query.isLoading) return { isLoading: true, attachment: null };
  return { isLoading: false, attachment: query.data ?? null };
}

function ResolveLoading() {
  return (
    <Row p="2xl" justify="center" align="center">
      <Spinner size="md" />
    </Row>
  );
}

function ResolveError({ onClose }: { onClose: () => void }) {
  const t = useTranslations('Feed');
  return (
    <Stack p="lg" gap="md">
      <Body color="destructive">
        {t('share.resolveError')}
      </Body>
      <Box style={{ textAlign: 'end' }}>
        <Button variant="ghost" size="md" onClick={onClose}>
          {t('share.close')}
        </Button>
      </Box>
    </Stack>
  );
}

// ── Toolbar Copy-link ────────────────────────────────────────────────────

function CopyLinkButton({
  href,
  kind,
}: {
  href: string;
  kind: AttachableInputKind;
}) {
  const t = useTranslations('Feed');
  // Stays "Copied" for the life of the open dialog — the Dialog unmounts this
  // on close, so it resets to "Copy link" the next time share is opened.
  const [copied, setCopied] = useState(false);
  const onClick = () => {
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    const url = resolveShareUrl(href);
    void navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true);
        trackShareLinkCopied({ entity_type: kind });
      },
      () => {
        // clipboard permission denied — silent; the rest of the modal works
      },
    );
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      icon={copied ? I.check : I.link}
      onClick={onClick}
      aria-label={copied ? t('share.copiedAria') : t('share.copyLinkAria')}
    >
      {copied ? t('share.copied') : t('share.copyLink')}
    </Button>
  );
}
